import React, { useEffect, useState, useCallback } from "react";
import saApi from "../superadminApi";
import { toast } from "sonner";
import {
  Plug, ShieldCheck, KeyRound, RefreshCw, Ban, Copy, Building2,
  Activity, CheckCircle2, X, Plus, Power, Clock, Snowflake, Webhook, Send, Trash2,
  FlaskConical, Play,
} from "lucide-react";

const SCOPES = [
  { id: "esg:workforce:read", label: "Workforce (B8)" },
  { id: "esg:movements:read", label: "Movimenti (B8)" },
  { id: "esg:working_hours:read", label: "Ore lavorate (B9)" },
  { id: "esg:training:read", label: "Formazione (B10)" },
  { id: "esg:collective_bargaining:read", label: "Contrattazione (B10)" },
  { id: "esg:management:read", label: "Management (C5)" },
  { id: "esg:shifts:read", label: "Turni" },
  { id: "esg:welfare:read", label: "Welfare" },
  { id: "esg:safety:read", label: "Sicurezza (B9)" },
  { id: "esg:remuneration:read", label: "Retribuzione (B10)" },
];
const WH_EVENTS = [
  { id: "workforce.updated", label: "Organico aggiornato" },
  { id: "training.updated", label: "Formazione aggiornata" },
];
const AVAIL_STYLE = {
  AVAILABLE: "bg-[#009640]/20 text-emerald-300 border-emerald-500/30",
  PARTIAL: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  NOT_AVAILABLE: "bg-red-500/15 text-red-300 border-red-500/30",
};
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiato negli appunti"); };
const demoInput = "bg-[#0b1220] border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#ff7e00]";
const DEMO_ENDPOINTS = [
  { id: "workforce", label: "Workforce (B8)" },
  { id: "movements", label: "Movimenti (B8)" },
  { id: "working-hours", label: "Ore lavorate (B9)" },
  { id: "training", label: "Formazione (B10)" },
  { id: "collective-bargaining", label: "Contrattazione (B10)" },
  { id: "management", label: "Management (C5)" },
  { id: "shifts", label: "Turni" },
  { id: "welfare", label: "Welfare" },
  { id: "safety", label: "Sicurezza (non disp.)" },
  { id: "remuneration", label: "Retribuzione (non disp.)" },
  { id: "capabilities", label: "Capabilities (per azienda)" },
];
const _thisYear = new Date().getFullYear();
const Badge = ({ children, cls }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>
);

const EsgIntegrationPanel = () => {
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", scopes: [], company_external_ids: [] });
  const [showWh, setShowWh] = useState(false);
  const [wh, setWh] = useState({ name: "", url: "", events: [], company_external_ids: [] });
  const [secret, setSecret] = useState(null); // { title, hint, entries:[{label,value}] }
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState({ external_id: "", endpoint: "workforce", date_from: `${_thisYear}-01-01`, date_to: `${_thisYear}-12-31`, updated_since: "" });
  const [demoRes, setDemoRes] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);

  const load = useCallback(() => {
    saApi.get("/superadmin/esg/overview").then((r) => setOverview(r.data)).catch(() => {});
    saApi.get("/superadmin/esg/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    saApi.get("/superadmin/esg/clients").then((r) => setClients(r.data || [])).catch(() => {});
    saApi.get("/superadmin/esg/logs").then((r) => setLogs(r.data || [])).catch(() => {});
    saApi.get("/superadmin/esg/webhooks").then((r) => setWebhooks(r.data || [])).catch(() => {});
    saApi.get("/superadmin/esg/webhook-deliveries").then((r) => setDeliveries(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const first = companies.find((c) => c.enabled && c.external_id);
    if (first) setDemo((d) => (d.external_id ? d : { ...d, external_id: first.external_id }));
  }, [companies]);

  const toggleCompany = async (c) => {
    try {
      await saApi.post(`/superadmin/esg/companies/${c.company_code}/${c.enabled ? "disable" : "enable"}`);
      toast.success(`Integrazione ${c.enabled ? "disabilitata" : "abilitata"} per ${c.company_label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const freezeYear = async (c) => {
    const y = window.prompt(`Congela l'anno per ${c.company_label} (fotografia immutabile dei dati ESG):`, String(new Date().getFullYear()));
    if (!y) return;
    try { await saApi.post(`/superadmin/esg/companies/${c.company_code}/freeze`, { year: Number(y) }); toast.success(`Anno ${y} congelato`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const unfreezeYear = async (c, year) => {
    if (!window.confirm(`Scongelare l'anno ${year} di ${c.company_label}? L'API tornerà a calcolare i dati live.`)) return;
    await saApi.post(`/superadmin/esg/companies/${c.company_code}/unfreeze`, { year }); toast.success(`Anno ${year} scongelato`); load();
  };

  const createClient = async () => {
    if (!form.name.trim()) return toast.error("Inserisci un nome");
    if (!form.scopes.length) return toast.error("Seleziona almeno uno scope");
    if (!form.company_external_ids.length) return toast.error("Seleziona almeno un'azienda");
    setBusy(true);
    try {
      const r = await saApi.post("/superadmin/esg/clients", form);
      setSecret({ title: "Credenziali client", hint: "Copia ora il client_secret: non sarà più mostrato.", entries: [{ label: "Client ID", value: r.data.client_id }, { label: "Client Secret", value: r.data.client_secret }] });
      setShowForm(false); setForm({ name: "", scopes: [], company_external_ids: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const revoke = async (cid) => { if (!window.confirm("Revocare questo client? I token esistenti smetteranno subito di funzionare.")) return; await saApi.post(`/superadmin/esg/clients/${cid}/revoke`); toast.success("Client revocato"); load(); };
  const rotate = async (cid) => { if (!window.confirm("Ruotare il secret? Il secret attuale e i token esistenti verranno invalidati.")) return; const r = await saApi.post(`/superadmin/esg/clients/${cid}/rotate`); setSecret({ title: "Nuovo secret", hint: "Copia ora il nuovo client_secret.", entries: [{ label: "Client ID", value: cid }, { label: "Client Secret", value: r.data.client_secret }] }); load(); };

  const createWebhook = async () => {
    if (!wh.name.trim() || !wh.url.trim()) return toast.error("Nome e URL obbligatori");
    if (!wh.events.length) return toast.error("Seleziona almeno un evento");
    if (!wh.company_external_ids.length) return toast.error("Seleziona almeno un'azienda");
    setBusy(true);
    try {
      const r = await saApi.post("/superadmin/esg/webhooks", wh);
      setSecret({ title: "Signing secret webhook", hint: "Configura questo secret nel ricevitore ESG PMI per verificare la firma HMAC (header X-ESG-Signature). Mostrato una sola volta.", entries: [{ label: "Signing secret", value: r.data.secret }] });
      setShowWh(false); setWh({ name: "", url: "", events: [], company_external_ids: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const testWebhook = async (id) => { try { const r = await saApi.post(`/superadmin/esg/webhooks/${id}/test`); r.data.success ? toast.success(`Test consegnato (HTTP ${r.data.status})`) : toast.error(`Consegna fallita (HTTP ${r.data.status || "?"})`); load(); } catch (e) { toast.error("Errore invio test"); } };
  const deleteWebhook = async (id) => { if (!window.confirm("Eliminare questo webhook?")) return; await saApi.delete(`/superadmin/esg/webhooks/${id}`); toast.success("Webhook eliminato"); load(); };

  const runDemo = async () => {
    setDemoBusy(true); setDemoRes(null);
    try {
      const r = await saApi.post("/superadmin/esg/demo/run", demo);
      setDemoRes(r.data);
      if (r.data?.token?.status !== 200) toast.error(`Token fallito (HTTP ${r.data?.token?.status || "?"})`);
      else if ((r.data?.data?.status ?? 500) < 400) toast.success(`Chiamata riuscita (HTTP ${r.data.data.status})`);
      else toast.error(`Endpoint HTTP ${r.data?.data?.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore esecuzione demo"); }
    finally { setDemoBusy(false); }
  };

  const enabledCompanies = companies.filter((c) => c.enabled && c.external_id);
  const extLabel = (ext) => enabledCompanies.find((c) => c.external_id === ext)?.company_label || (ext || "").slice(0, 8);

  return (
    <div className="mb-10" data-testid="sa-esg-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Plug size={18} className="text-[#ff7e00]" /> Integrazione ESG</h2>
        <div className="flex items-center gap-2">
          <Badge cls="bg-[#009640]/20 text-emerald-300 border-emerald-500/30"><ShieldCheck size={11} /> API {overview?.status || "READY"}</Badge>
          <Badge cls="bg-white/10 text-gray-300 border-white/15">Connessione: {overview?.connectionStatus || "NOT_CONNECTED"}</Badge>
        </div>
      </div>
      <p className="text-gray-400 text-[12px] mb-4 max-w-3xl">API sicura, versionata e in sola lettura che espone alla piattaforma <b className="text-gray-200">ESG PMI</b> esclusivamente dati HR aggregati (nessun dato personale). Base: <code className="text-[#ff7e00]">/api/integrations/esg/v1</code>.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { t: "Client attivi", v: `${overview?.clients_active ?? 0}/${overview?.clients_total ?? 0}`, icon: KeyRound, tid: "sa-esg-stat-clients" },
          { t: "Aziende abilitate", v: overview?.companies_enabled ?? 0, icon: Building2, tid: "sa-esg-stat-companies" },
          { t: "Richieste (24h)", v: overview?.requests_last_24h ?? 0, icon: Activity, tid: "sa-esg-stat-requests" },
          { t: "Dati collegabili", v: (overview?.capabilities || []).filter((c) => c.availability !== "NOT_AVAILABLE").length, icon: CheckCircle2, tid: "sa-esg-stat-capabilities" },
        ].map((s) => (
          <div key={s.tid} data-testid={s.tid} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <s.icon size={18} className="text-[#ff7e00] mb-2" />
            <div className="text-white text-[24px] font-black leading-none">{s.v}</div>
            <div className="text-gray-400 text-[12px] mt-1">{s.t}</div>
          </div>
        ))}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6" data-testid="sa-esg-capabilities">
        <div className="text-gray-300 text-[13px] font-bold mb-3">Dati Social collegabili (disponibilità reale in JOBTIMEHR)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(overview?.capabilities || []).map((c) => (
            <div key={c.domain} data-testid={`sa-esg-cap-${c.domain}`} className="flex items-start justify-between gap-2 bg-white/5 rounded-xl px-3 py-2">
              <div className="min-w-0">
                <div className="text-white text-[12.5px] font-semibold">{c.domain} <span className="text-gray-500">· {(c.vsme || []).join("/")}</span></div>
                <div className="text-gray-500 text-[11px] leading-tight">{c.note}</div>
              </div>
              <Badge cls={AVAIL_STYLE[c.availability] || AVAIL_STYLE.NOT_AVAILABLE}>{c.availability}</Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Companies + freeze */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mb-6">
        <div className="px-5 py-3 text-gray-300 text-[13px] font-bold border-b border-white/10">Aziende abilitate all'integrazione</div>
        <table className="w-full text-left">
          <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
            <tr><th className="px-5 py-2.5">Azienda</th><th className="px-3 py-2.5">Dip.</th><th className="px-3 py-2.5">External ID</th><th className="px-3 py-2.5">Anni congelati</th><th className="px-3 py-2.5 text-right">Stato</th></tr>
          </thead>
          <tbody className="text-[13px]">
            {companies.map((c) => (
              <tr key={c.company_code} data-testid={`sa-esg-company-${c.company_code}`} className="border-t border-white/5">
                <td className="px-5 py-3"><div className="text-white font-bold">{c.company_label}</div><div className="text-gray-500 text-[11px]">{c.company_code}</div></td>
                <td className="px-3 py-3 text-gray-300">{c.headcount}</td>
                <td className="px-3 py-3">{c.external_id ? <button onClick={() => copy(c.external_id)} className="text-gray-400 hover:text-white text-[11px] font-mono flex items-center gap-1" title="Copia external ID"><Copy size={11} /> {c.external_id.slice(0, 8)}…</button> : <span className="text-gray-600 text-[11px]">—</span>}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap items-center gap-1">
                    {(c.frozen_years || []).map((y) => (
                      <button key={y} onClick={() => unfreezeYear(c, y)} data-testid={`sa-esg-frozen-${c.company_code}-${y}`} title="Scongela" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30 hover:bg-red-500/20 hover:text-red-300">
                        <Snowflake size={10} /> {y} <X size={9} />
                      </button>
                    ))}
                    {c.enabled && <button onClick={() => freezeYear(c)} data-testid={`sa-esg-freeze-${c.company_code}`} className="text-[11px] text-gray-400 hover:text-white border border-white/15 rounded-full px-2 py-0.5">+ Congela</button>}
                  </div>
                </td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => toggleCompany(c)} data-testid={`sa-esg-toggle-${c.company_code}`} className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-lg transition-colors ${c.enabled ? "bg-[#009640]/20 text-emerald-300 hover:bg-red-500/20 hover:text-red-300" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}>
                    <Power size={13} /> {c.enabled ? "Abilitata" : "Disabilitata"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Clients */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-gray-300 text-[13px] font-bold">Client autorizzati (ESG PMI)</div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="sa-esg-new-client-btn" disabled={!enabledCompanies.length} className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e86f00] disabled:opacity-40 text-white text-[12px] font-bold px-3 py-1.5 rounded-lg"><Plus size={14} /> Nuovo client</button>
      </div>
      {!enabledCompanies.length && <div className="text-gray-500 text-[12px] mb-3">Abilita prima almeno un'azienda per creare un client.</div>}
      {showForm && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4" data-testid="sa-esg-client-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome client (es. ESG PMI produzione)" data-testid="sa-esg-client-name" className="w-full bg-[#0b1220] border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#ff7e00] mb-3" />
          <div className="text-gray-400 text-[11px] font-bold uppercase mb-1.5">Scopes</div>
          <div className="grid sm:grid-cols-2 gap-1.5 mb-3">
            {SCOPES.map((s) => { const on = form.scopes.includes(s.id); return (
              <button key={s.id} onClick={() => setForm({ ...form, scopes: on ? form.scopes.filter((x) => x !== s.id) : [...form.scopes, s.id] })} data-testid={`sa-esg-scope-${s.id}`} className={`text-left text-[12px] px-3 py-1.5 rounded-lg border ${on ? "bg-[#ff7e00]/15 border-[#ff7e00]/40 text-white" : "bg-white/5 border-white/10 text-gray-400"}`}>{on ? "☑" : "☐"} {s.label}</button>
            ); })}
          </div>
          <div className="text-gray-400 text-[11px] font-bold uppercase mb-1.5">Aziende autorizzate</div>
          <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
            {enabledCompanies.map((c) => { const on = form.company_external_ids.includes(c.external_id); return (
              <button key={c.company_code} onClick={() => setForm({ ...form, company_external_ids: on ? form.company_external_ids.filter((x) => x !== c.external_id) : [...form.company_external_ids, c.external_id] })} data-testid={`sa-esg-client-company-${c.company_code}`} className={`text-left text-[12px] px-3 py-1.5 rounded-lg border ${on ? "bg-[#0d6cb8]/20 border-[#0d6cb8]/50 text-white" : "bg-white/5 border-white/10 text-gray-400"}`}>{on ? "☑" : "☐"} {c.company_label}</button>
            ); })}
          </div>
          <div className="flex gap-2">
            <button onClick={createClient} disabled={busy} data-testid="sa-esg-client-create" className="bg-[#009640] hover:bg-[#007a34] disabled:opacity-50 text-white text-[13px] font-bold px-4 py-2 rounded-lg">Crea e genera secret</button>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white text-[13px] font-semibold px-3 py-2">Annulla</button>
          </div>
        </div>
      )}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-2 mb-6" data-testid="sa-esg-clients-list">
        {!clients.length && <div className="text-gray-500 text-[12px] px-4 py-3">Nessun client configurato.</div>}
        {clients.map((c) => (
          <div key={c.client_id} data-testid={`sa-esg-client-${c.client_id}`} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 border-b border-white/5 last:border-0">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.active ? "bg-[#009640]/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}><KeyRound size={15} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[13px] font-bold flex items-center gap-2">{c.name}{c.active ? <Badge cls="bg-[#009640]/20 text-emerald-300 border-emerald-500/30">Attivo</Badge> : <Badge cls="bg-red-500/15 text-red-300 border-red-500/30">Revocato</Badge>}<span className="text-gray-500 text-[10px] font-normal">v{c.credential_version}</span></div>
              <div className="text-gray-500 text-[11px] font-mono truncate">{c.client_id} · {(c.scopes || []).length} scope · {(c.company_external_ids || []).length} azienda/e</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button title="Ruota secret" onClick={() => rotate(c.client_id)} data-testid={`sa-esg-rotate-${c.client_id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><RefreshCw size={15} /></button>
              {c.active && <button title="Revoca" onClick={() => revoke(c.client_id)} data-testid={`sa-esg-revoke-${c.client_id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Ban size={15} /></button>}
            </div>
          </div>
        ))}
      </div>

      {/* Webhooks */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-gray-300 text-[13px] font-bold flex items-center gap-2"><Webhook size={15} /> Webhook (notifiche push a ESG PMI)</div>
        <button onClick={() => setShowWh((v) => !v)} data-testid="sa-esg-new-webhook-btn" disabled={!enabledCompanies.length} className="flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-200 text-[12px] font-bold px-3 py-1.5 rounded-lg disabled:opacity-40"><Plus size={14} /> Nuovo webhook</button>
      </div>
      {showWh && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4" data-testid="sa-esg-webhook-form">
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <input value={wh.name} onChange={(e) => setWh({ ...wh, name: e.target.value })} placeholder="Nome" data-testid="sa-esg-wh-name" className="bg-[#0b1220] border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#ff7e00]" />
            <input value={wh.url} onChange={(e) => setWh({ ...wh, url: e.target.value })} placeholder="https://esg-pmi.example.com/webhooks/jobtimehr" data-testid="sa-esg-wh-url" className="bg-[#0b1220] border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white font-mono outline-none focus:border-[#ff7e00]" />
          </div>
          <div className="text-gray-400 text-[11px] font-bold uppercase mb-1.5">Eventi</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {WH_EVENTS.map((ev) => { const on = wh.events.includes(ev.id); return (
              <button key={ev.id} onClick={() => setWh({ ...wh, events: on ? wh.events.filter((x) => x !== ev.id) : [...wh.events, ev.id] })} data-testid={`sa-esg-wh-event-${ev.id}`} className={`text-[12px] px-3 py-1.5 rounded-lg border ${on ? "bg-[#ff7e00]/15 border-[#ff7e00]/40 text-white" : "bg-white/5 border-white/10 text-gray-400"}`}>{on ? "☑" : "☐"} {ev.label}</button>
            ); })}
          </div>
          <div className="text-gray-400 text-[11px] font-bold uppercase mb-1.5">Aziende</div>
          <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
            {enabledCompanies.map((c) => { const on = wh.company_external_ids.includes(c.external_id); return (
              <button key={c.company_code} onClick={() => setWh({ ...wh, company_external_ids: on ? wh.company_external_ids.filter((x) => x !== c.external_id) : [...wh.company_external_ids, c.external_id] })} data-testid={`sa-esg-wh-company-${c.company_code}`} className={`text-left text-[12px] px-3 py-1.5 rounded-lg border ${on ? "bg-[#0d6cb8]/20 border-[#0d6cb8]/50 text-white" : "bg-white/5 border-white/10 text-gray-400"}`}>{on ? "☑" : "☐"} {c.company_label}</button>
            ); })}
          </div>
          <div className="flex gap-2">
            <button onClick={createWebhook} disabled={busy} data-testid="sa-esg-wh-create" className="bg-[#009640] hover:bg-[#007a34] disabled:opacity-50 text-white text-[13px] font-bold px-4 py-2 rounded-lg">Crea webhook</button>
            <button onClick={() => setShowWh(false)} className="text-gray-400 hover:text-white text-[13px] font-semibold px-3 py-2">Annulla</button>
          </div>
        </div>
      )}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-2 mb-4" data-testid="sa-esg-webhooks-list">
        {!webhooks.length && <div className="text-gray-500 text-[12px] px-4 py-3">Nessun webhook configurato.</div>}
        {webhooks.map((w) => (
          <div key={w.id} data-testid={`sa-esg-webhook-${w.id}`} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 border-b border-white/5 last:border-0">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#7c3aed]/15 text-purple-300"><Webhook size={15} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[13px] font-bold">{w.name}</div>
              <div className="text-gray-500 text-[11px] font-mono truncate">{w.url}</div>
              <div className="text-gray-500 text-[10px]">{(w.events || []).join(", ")} · {(w.company_external_ids || []).map(extLabel).join(", ")}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button title="Invia evento di test" onClick={() => testWebhook(w.id)} data-testid={`sa-esg-wh-test-${w.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Send size={15} /></button>
              <button title="Elimina" onClick={() => deleteWebhook(w.id)} data-testid={`sa-esg-wh-delete-${w.id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {!!deliveries.length && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6" data-testid="sa-esg-deliveries">
          <div className="text-gray-300 text-[13px] font-bold mb-2">Ultime consegne webhook</div>
          <div className="space-y-1 max-h-40 overflow-auto">
            {deliveries.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-mono">
                <span className={d.success ? "text-emerald-400" : "text-red-400"}>{d.success ? "OK" : "KO"} {d.status || ""}</span>
                <span className="text-gray-500">{(d.ts || "").slice(0, 19).replace("T", " ")}</span>
                <span className="text-gray-300">{d.event}</span>
                <span className="text-gray-600 truncate">{d.url}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Access log */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="sa-esg-logs">
        <div className="text-gray-300 text-[13px] font-bold mb-2 flex items-center gap-2"><Clock size={14} /> Ultime richieste API</div>
        {!logs.length && <div className="text-gray-500 text-[12px]">Nessuna richiesta registrata.</div>}
        <div className="space-y-1 max-h-56 overflow-auto">
          {logs.map((l, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] text-gray-400 font-mono">
              <span className={l.status < 400 ? "text-emerald-400" : "text-red-400"}>{l.status}</span>
              <span className="text-gray-500">{(l.ts || "").slice(0, 19).replace("T", " ")}</span>
              <span className="text-gray-300">{l.method} {l.endpoint}</span>
              <span className="text-gray-600 truncate">{l.client_id}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Test client demo (simula ESG PMI) */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mt-6" data-testid="sa-esg-demo">
        <div className="text-gray-300 text-[13px] font-bold mb-1 flex items-center gap-2"><FlaskConical size={15} className="text-[#ff7e00]" /> Test client demo (simula ESG PMI)</div>
        <p className="text-gray-500 text-[11px] mb-3 max-w-3xl">Esegue il flusso reale end-to-end: OAuth token + chiamata HTTP all'endpoint scelto, tramite un client demo interno (secret gestito solo lato server, mai esposto). Utile per validare l'integrazione.</p>
        {!enabledCompanies.length ? (
          <div className="text-gray-500 text-[12px]">Abilita almeno un'azienda per usare il test.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
              <select value={demo.external_id} onChange={(e) => setDemo({ ...demo, external_id: e.target.value })} data-testid="sa-esg-demo-company" className={demoInput}>
                {enabledCompanies.map((c) => <option key={c.company_code} value={c.external_id} style={{ color: "#111" }}>{c.company_label}</option>)}
              </select>
              <select value={demo.endpoint} onChange={(e) => setDemo({ ...demo, endpoint: e.target.value })} data-testid="sa-esg-demo-endpoint" className={demoInput}>
                {DEMO_ENDPOINTS.map((ep) => <option key={ep.id} value={ep.id} style={{ color: "#111" }}>{ep.label}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={demo.date_from} onChange={(e) => setDemo({ ...demo, date_from: e.target.value })} data-testid="sa-esg-demo-from" className={`${demoInput} [color-scheme:dark]`} />
                <input type="date" value={demo.date_to} onChange={(e) => setDemo({ ...demo, date_to: e.target.value })} data-testid="sa-esg-demo-to" className={`${demoInput} [color-scheme:dark]`} />
              </div>
            </div>
            <button onClick={runDemo} disabled={demoBusy || !demo.external_id} data-testid="sa-esg-demo-run" className="flex items-center gap-2 bg-[#0d6cb8] hover:bg-[#0b5c9e] disabled:opacity-50 text-white text-[13px] font-bold px-4 py-2 rounded-lg">
              <Play size={15} /> {demoBusy ? "Esecuzione…" : "Esegui chiamata"}
            </button>
            {demoRes && (
              <div className="mt-4" data-testid="sa-esg-demo-result">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge cls={demoRes.token?.status === 200 ? "bg-[#009640]/20 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}>Token HTTP {demoRes.token?.status} · {demoRes.token?.elapsed_ms}ms</Badge>
                  {demoRes.data && <Badge cls={demoRes.data.status < 400 ? "bg-[#009640]/20 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}>Dati HTTP {demoRes.data.status} · {demoRes.data.elapsed_ms}ms</Badge>}
                  {demoRes.token?.scope && <span className="text-gray-500 text-[11px] font-mono truncate max-w-full">scope: {demoRes.token.scope}</span>}
                </div>
                <pre data-testid="sa-esg-demo-json" className="bg-[#0b1220] border border-white/10 rounded-lg p-3 text-[11px] text-emerald-200 font-mono overflow-auto max-h-80 whitespace-pre-wrap break-all">{JSON.stringify(demoRes.data ? demoRes.data.body : (demoRes.token?.error || demoRes), null, 2)}</pre>
              </div>
            )}
          </>
        )}
      </div>

      {/* Secret modal (generico, mostrato una volta) */}
      {secret && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="sa-esg-secret-modal">
          <div className="bg-[#111a2e] border border-white/15 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-black text-[16px] flex items-center gap-2"><KeyRound size={18} className="text-[#ff7e00]" /> {secret.title}</h3>
              <button onClick={() => setSecret(null)} className="text-gray-400 hover:text-white" data-testid="sa-esg-secret-close"><X size={18} /></button>
            </div>
            <p className="text-amber-300 text-[12px] mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">⚠️ {secret.hint}</p>
            {secret.entries.map((e, i) => (
              <div key={i} className="mb-3">
                <label className="text-gray-400 text-[11px] font-bold uppercase">{e.label}</label>
                <div className="flex items-center gap-2">
                  <code data-testid={`sa-esg-secret-${i}`} className="flex-1 bg-[#0b1220] border border-white/10 rounded-lg px-3 py-2 text-[12px] text-emerald-300 font-mono break-all">{e.value}</code>
                  <button onClick={() => copy(e.value)} className="p-2 rounded-lg bg-white/10 text-gray-300 hover:text-white"><Copy size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EsgIntegrationPanel;
